import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
} from "antd";
import { MinusOutlined, PlusOutlined, MenuOutlined } from "@ant-design/icons";
import { CALL_API } from "common/API";
import { NON_EMPTY_REGEX, STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveIsShowAgain } from "redux/actions";
import { getClientid, getIsShowAgain } from "redux/selectors";
import styled from "styled-components";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(isBetween);

const StyledModal = styled(Modal)`
  .box {
    background: #e3e8fc;
    border-radius: 5px;
  }
  .ant-modal-body {
    overflow: visible !important;
  }
  .ant-modal-content {
    overflow: visible !important;
  }
  .day-picker {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 8px;
  }
  .day-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1.5px solid #d9d9d9;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: #5d5f68;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    user-select: none;
  }
  .day-btn.selected {
    background: #2d53da;
    border-color: #2d53da;
    color: #fff;
  }
  .weekly-pattern {
    background: #f5f7ff;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    overflow: visible;
  }
  .pattern-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .arrange-btn {
    color: #5d5f68;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: none;
    padding: 0;
  }
  .add-day-btn {
    color: #2d53da;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    font-size: 13px;
    border: none;
    background: none;
    padding: 0;
  }
`;

const DAY_OPTIONS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

const WEEK_OPTIONS = [
  {
    label: "1st Week",
    value: 1,
  },
  {
    label: "2nd Week",
    value: 2,
  },
  {
    label: "3rd Week",
    value: 3,
  },
  {
    label: "4th Week",
    value: 4,
  },
  {
    label: "5th Week",
    value: 5,
  },
];

const FREQUENCY_OPTIONS = [
  { label: "Every Day", value: "everyday" },
  { label: "Custom Days", value: "custom" },
  { label: "Weekdays", value: "weekdays" },
  { label: "Weekends", value: "weekends" },
];

const defaultPatternRow = () => ({
  id: Date.now() + Math.random(),
  day: null,
  shiftId: null,
  applyEvery: [],
});

const AssignShift = ({
  open,
  setOpen,
  selectedCell,
  shiftList,
  getAssignedShiftList,
  selectedRowIds,
  isMonthly,
  selectedCells = [],
  handleCancel = () => {},
  currentTime,
  userTimeZone,
  dataSource,
  selectedDate,
}) => {
  const ClientId = useSelector(getClientid);
  const getShowAgain = useSelector(getIsShowAgain);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const containerRef = useRef(null);

  const [loader, setLoader] = useState(false);
  const [isDateCheckLoader, setIsDateCheckLoader] = useState(false);
  const [isShiftFound, setIsShiftFount] = useState(false);
  const [setupType, setSetupType] = useState("daily");
  const [frequency, setFrequency] = useState("everyday");
  const [selectedDays, setSelectedDays] = useState([]);
  const [applyEvery, setApplyEvery] = useState([]);
  const [patternRows, setPatternRows] = useState([defaultPatternRow()]);

  const isMultiSelect = selectedRowIds?.length > 0;

  const getWeekOfMonth = (date) => {
    const firstDay = dayjs(date).startOf("month");
    const firstWeekDay = firstDay.day(); // 0 = Sunday

    return Math.ceil((date.date() + firstWeekDay) / 7);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const restrictPastDate = (current, type) => {
    const fromDate = form.getFieldValue("fromdate");
    if (type === "todate") {
      return current && fromDate && current < moment(fromDate).startOf("day");
    }
    return current && current < moment().startOf("day");
  };

  const findShift = shiftList?.find(
    (val) => val?.shift_id === form.getFieldValue("shifttype")
  );
  const getShiftById = (shiftId) =>
    shiftList?.find((val) => val?.shift_id === shiftId);

  const shiftOptions =
    shiftList
      ?.filter((item) => item?.status === "active")
      ?.map((val) => ({
        label: `${val?.shifttitle}   (${moment(val?.starts).format(
          "hh:mm A"
        )} - ${moment(val?.ends).format("hh:mm A")})`,
        value: val?.shift_id,
      })) || [];

  const checkInBetweenDates = async () => {
    try {
      setIsDateCheckLoader(true);
      const payload = {
        clientId: ClientId,
        userId: selectedRowIds?.length
          ? selectedRowIds
          : [selectedCell?.userid],
        from: dayjs(form.getFieldValue("fromdate")).format("YYYY-MM-DD"),
        to: dayjs(form.getFieldValue("todate")).format("YYYY-MM-DD"),
      };
      const { assigned } = await CALL_API(
        `check-assigned-shift`,
        "post",
        payload
      );
      if (assigned === true || assigned === false) {
        setIsShiftFount(assigned);
      }
    } catch (error) {
      console.log("[checkInBetweenDates] Error:", error);
    } finally {
      setIsDateCheckLoader(false);
    }
  };

  const currentDate = dayjs().tz(userTimeZone).format("YYYY-MM-DD");
  const shiftStartTime = dayjs(findShift?.starts).format("HH:mm:ss");

  const selectedData =
    dataSource
      ?.filter((item) => selectedRowIds?.includes(item?.userid))
      ?.map(
        (item) => item?.[`${dayjs().tz(userTimeZone).format("YYYY-MM-DD")}`]
      ) || [];

  const isInvalid =
    dayjs(form.getFieldValue("fromdate")).format("YYYY-MM-DD") ===
      currentDate && currentTime >= shiftStartTime;

  const filterSelectedCells = selectedCells.filter(
    (item) => item?.colKey === currentDate
  );
  const isInvalidSelectedCells =
    filterSelectedCells?.length > 0 && currentTime >= shiftStartTime;

  // ── Day toggle ───────────────────────────────────────────────────────────
  const toggleDay = (dayValue) => {
    setSelectedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue]
    );
  };

  // ── Weekly pattern row handlers ──────────────────────────────────────────
  const addPatternRow = () => {
    const lastRow = patternRows[patternRows.length - 1];

    if (!lastRow.day || !lastRow.shiftId) {
      message.error("Please complete the current row before adding a new one");
      return;
    }

    setPatternRows((prev) => [...prev, defaultPatternRow()]);
  };

  const removePatternRow = (id) => {
    setPatternRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updatePatternRow = (id, field, value) => {
    setPatternRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const resetExtraFields = () => {
    setSetupType("daily");
    setFrequency("everyday");
    setSelectedDays([]);
    setApplyEvery([]);
    setPatternRows([defaultPatternRow()]);
  };

  // ── Build userDateArray ──────────────────────────────────────────────────
  const buildUserDateArray = (values) => {
    if (selectedCells?.length > 0) return selectedCells;

    const fromDate = dayjs(values.fromdate);
    const toDate = dayjs(values.todate);

    const allDates = [];
    let current = fromDate;
    while (current.isBefore(toDate, "day") || current.isSame(toDate, "day")) {
      allDates.push(current);
      current = current.add(1, "day");
    }

    // ── Weekly setup ──
    if (setupType === "weekly") {
      const result = [];

      for (const row of patternRows) {
        if (row.day === null || !row.shiftId) continue;

        const shift = getShiftById(row.shiftId);

        allDates.forEach((date) => {
          const isSameDay = date.day() === row.day;

          if (!isSameDay) return;

          if (row.applyEvery.length > 0) {
            const weekNum = getWeekOfMonth(date);

            if (!row.applyEvery.includes(weekNum)) return;
          }

          selectedRowIds.forEach((uid) => {
            result.push({
              userid: uid,
              colKey: date.format("YYYY-MM-DD"),
              shiftid: row.shiftId, // ✅ MUST NOT be empty
              shifttitle: shift?.shifttitle || "", // ✅ avoid null
            });
          });
        });
      }

      return result;
    }

    // ── Daily setup ──
    const shift = getShiftById(values.shifttype);

    let filteredDates = allDates;
    if (frequency === "weekdays") {
      filteredDates = allDates.filter((d) => d.day() >= 1 && d.day() <= 5);
    } else if (frequency === "weekends") {
      filteredDates = allDates.filter((d) => d.day() === 0 || d.day() === 6);
    } else if (frequency === "custom") {
      filteredDates = allDates.filter((date) => {
        if (!selectedDays.includes(date.day())) return false;
        if (applyEvery.length === 0) return true;
        const weekNum = getWeekOfMonth(date);
        return applyEvery.includes(weekNum);
      });
    }

    const result = [];
    filteredDates.forEach((date) => {
      selectedRowIds.forEach((uid) => {
        result.push({
          userid: uid,
          colKey: date.format("YYYY-MM-DD"),
          shiftid: values.shifttype || "",
          shifttitle: shift?.shifttitle || null,
        });
      });
    });
    return result;
  };

  // ── onFinish ─────────────────────────────────────────────────────────────
  const onFinish = async (values) => {
    try {
      setLoader(true);

      if (isMultiSelect && setupType === "weekly") {
        const hasInvalidRow = patternRows.some(
          (r) => r.day === null || !r.shiftId
        );
        if (hasInvalidRow) {
          message.error("Please fill all rows in the shift pattern");
          setLoader(false);
          return;
        }
      }

      if (
        isMultiSelect &&
        setupType === "daily" &&
        frequency === "custom" &&
        selectedDays.length === 0
      ) {
        message.error("Please select at least one custom day");
        setLoader(false);
        return;
      }

      const computedUserDateArray = isMultiSelect
        ? buildUserDateArray(values)
        : selectedCells?.length > 0
        ? selectedCells
        : [];

      if (isMultiSelect && computedUserDateArray.length === 0) {
        message.error("No dates match the selected criteria");
        setLoader(false);
        return;
      }

      console.log("userDateArray:", computedUserDateArray);

      const payload = {
        clientId: ClientId,
        userId:
          selectedCells?.length > 0
            ? []
            : selectedRowIds?.length
            ? selectedRowIds
            : [selectedCell?.userid],
        from: dayjs(values.fromdate).format("YYYY-MM-DD"),
        to: dayjs(values.todate).format("YYYY-MM-DD"),
        shift: values.shifttype || "",
        replaceExisting: values.replaceall || false,
        notifyByEmail: values.notifyemail || false,
        color: findShift?.color,
        userDateArray: computedUserDateArray,
        localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (isMultiSelect && setupType === "weekly") {
        payload.method = "assign_shift";
      }

      const response = await CALL_API(
        `assign-shift/${ClientId}`,
        "post",
        payload
      );

      if (
        response?.message === "Shift assigned successfully." ||
        response?.message === "Shift assignment completed."
      ) {
        getAssignedShiftList();
        message?.success("Shift assigned successfully");
        setIsShiftFount(false);
        form?.resetFields();
        resetExtraFields();
        handleCancel();
      } else {
        message?.error("Something went wrong");
      }
    } catch (error) {
      console.log("[onFinish] Error:", error);
    } finally {
      dispatch(
        saveIsShowAgain({ assignShift: form?.getFieldValue("assignshift") })
      );
      setLoader(false);
      setOpen(false);
    }
  };

  const showAssignConfirm = (values) => {
    if (getShowAgain?.assignShift) {
      onFinish(values);
    } else {
      Modal.confirm({
        title: "Are you sure you want to Assign this Shift?",
        content: (
          <Form
            form={form}
            name="isShowagain"
            layout="vertical"
            initialValues={{ assignshift: getShowAgain?.assignShift }}
          >
            <Form.Item name="assignshift" valuePropName="checked">
              <Checkbox
                disabled={false}
                style={{ color: "#5d5f68", fontWeight: 500 }}
              >
                Don't show this again
              </Checkbox>
            </Form.Item>
          </Form>
        ),
        okText: "Yes, Assign",
        cancelText: "No, Cancel",
        okButtonProps: { loading: loader },
        onOk: async () => {
          onFinish(values);
        },
      });
    }
  };

  const handleWarning = () => {
    Modal.warning({
      title: "Shift assign not allowed",
      content:
        "You did not assign any shift for today, and the shift start time has already passed.",
      okText: "OK",
    });
  };

  useEffect(() => {
    if (selectedRowIds?.length > 0) {
      form.setFieldValue(
        "fromdate",
        isMonthly
          ? dayjs(selectedDate).isBetween(
              dayjs().startOf("month"),
              dayjs().endOf("month"),
              "day",
              "[]"
            )
            ? dayjs()
            : dayjs(selectedDate).startOf("month")
          : dayjs(selectedDate).isBetween(
              dayjs().startOf("week"),
              dayjs().endOf("week"),
              "day",
              "[]"
            )
          ? dayjs()
          : dayjs(selectedDate).startOf("week")
      );
      form.setFieldValue(
        "todate",
        isMonthly
          ? dayjs(selectedDate).endOf("month")
          : dayjs(selectedDate).endOf("week")
      );
      checkInBetweenDates();
    } else if (selectedCell) {
      form.setFieldValue("fromdate", dayjs(selectedCell.date, "YYYY-MM-DD"));
      form.setFieldValue("todate", dayjs(selectedCell.date, "YYYY-MM-DD"));
    }
  }, [selectedRowIds, isMonthly, selectedCell, selectedDate]);

  return (
    <StyledModal
      title={
        <div className="d-flex justify-content-between align-items-center">
          <div>Assigning Shift</div>
          {selectedRowIds?.length > 0 && (
            <small className="mx-4" style={{ color: "#5d5f68" }}>
              Employee Chosen: <b>{selectedRowIds?.length}</b>
            </small>
          )}
          {selectedCells?.length > 0 && (
            <small className="mx-4" style={{ color: "#5d5f68" }}>
              Days Chosen: <b>{selectedCells?.length}</b>
            </small>
          )}
        </div>
      }
      open={open}
      getContainer={false}
      onCancel={() => {
        setIsShiftFount(false);
        form?.resetFields();
        resetExtraFields();
        handleCancel();
        setOpen(false);
      }}
      width={700}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            form.resetFields();
            setIsShiftFount(false);
            resetExtraFields();
            handleCancel();
            setOpen(false);
          }}
        >
          Cancel
        </Button>,
        <Button
          key="assign"
          type="primary"
          onClick={() => form.submit()}
          loading={loader}
        >
          Assign
        </Button>,
      ]}
    >
      <Spin spinning={isDateCheckLoader}>
        <div style={{ minHeight: "400px" }} ref={containerRef}>
          <Form
            form={form}
            name="assignShiftForm"
            layout="vertical"
            initialValues={{
              fromdate: selectedCell?.date
                ? dayjs(selectedCell.date, "YYYY-MM-DD")
                : null,
              todate: selectedCell?.date
                ? dayjs(selectedCell.date, "YYYY-MM-DD")
                : null,
            }}
            onFinish={(values) => {
              if (
                selectedCells?.length === 0 &&
                selectedRowIds?.length === 0 &&
                isInvalid
              ) {
                handleWarning();
                return;
              } else if (
                selectedRowIds?.length > selectedData?.length &&
                isInvalid
              ) {
                handleWarning();
              } else if (selectedCells?.length > 0 && isInvalidSelectedCells) {
                handleWarning();
              } else {
                showAssignConfirm(values);
              }
            }}
          >
            <Row gutter={20}>
              {/* ── Date range ── */}
              {selectedCells?.length === 0 && (
                <>
                  <Col span={24} style={{ color: "#5d5f68", fontWeight: 500 }}>
                    Specify the date range
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label="From"
                      name="fromdate"
                      rules={[
                        {
                          required: selectedCells?.length === 0,
                          message: "From date is required",
                        },
                        {
                          validator: (_, value) =>
                            value && dayjs(value).isValid()
                              ? Promise.resolve()
                              : Promise.reject("Invalid date"),
                        },
                      ]}
                    >
                      <DatePicker
                        format="DD-MM-YYYY"
                        className="w-100"
                        placeholder=""
                        disabledDate={(current) =>
                          restrictPastDate(current, "fromdate")
                        }
                        disabled={selectedRowIds?.length > 0 ? false : true}
                        getPopupContainer={() => containerRef.current}
                        onChange={(value) => {
                          const toDate = form.getFieldValue("todate");
                          if (toDate && value && dayjs(value).isAfter(toDate)) {
                            form.setFieldsValue({ todate: value });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={12}>
                    <Form.Item
                      label="To Date"
                      name="todate"
                      rules={[
                        {
                          required: selectedCells?.length === 0,
                          message: "Please select To Date",
                        },
                      ]}
                    >
                      <DatePicker
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        disabledDate={(current) =>
                          restrictPastDate(current, "fromdate")
                        }
                        getPopupContainer={() => containerRef.current}
                        onChange={checkInBetweenDates}
                      />
                    </Form.Item>
                  </Col>

                  {isShiftFound && (
                    <Col xs={24}>
                      <div className="box p-2 mb-2">
                        <Form.Item
                          label="Some chosen employees already have shifts in this date range."
                          name="replaceall"
                          rules={[
                            {
                              required:
                                selectedCells?.length === 0 && isShiftFound,
                              message: "Please select an option",
                            },
                          ]}
                        >
                          <Radio.Group>
                            <Radio value={true}>
                              Replace all with new shift
                            </Radio>
                            <Radio value={false}>
                              Only apply to Unassigned day
                            </Radio>
                          </Radio.Group>
                        </Form.Item>
                      </div>
                    </Col>
                  )}
                </>
              )}

              {/* ── Setup type ── */}
              {isMultiSelect && (
                <Col xs={24} className="mb-3">
                  <div
                    style={{
                      color: "#5d5f68",
                      fontWeight: 500,
                      marginBottom: 8,
                    }}
                  >
                    Choose the type of shift assignment
                    <span style={{ color: "red" }}> *</span>
                  </div>
                  <Radio.Group
                    value={setupType}
                    onChange={(e) => {
                      setSetupType(e.target.value);
                      setPatternRows([defaultPatternRow()]);
                      setSelectedDays([]);
                      setApplyEvery([]);
                      setFrequency("everyday");
                    }}
                  >
                    <Radio value="daily">Daily Setup</Radio>
                    <Radio value="weekly">Weekly Setup</Radio>
                  </Radio.Group>
                </Col>
              )}

              {/* ══════════════════════════════════════════
                  WEEKLY SETUP
              ══════════════════════════════════════════ */}
              {isMultiSelect && setupType === "weekly" && (
                <Col xs={24}>
                  <div className="weekly-pattern">
                    <div className="pattern-header">
                      <span style={{ color: "#5d5f68", fontWeight: 500 }}>
                        Create a shift pattern
                      </span>
                      <button className="arrange-btn" type="button">
                        <MenuOutlined /> Arrange Order
                      </button>
                    </div>

                    {patternRows.map((row) => (
                      <div
                        key={row.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 10,
                          flexWrap: "nowrap",
                        }}
                      >
                        {/* Day */}
                        <Select
                          placeholder="Day"
                          value={row.day}
                          onChange={(val) =>
                            updatePatternRow(row.id, "day", val)
                          }
                          style={{ width: 90 }}
                          options={DAY_OPTIONS.map((day) => ({
                            ...day,
                            disabled: patternRows.some(
                              (r) => r.day === day.value && r.id !== row.id
                            ),
                          }))}
                        />

                        <span style={{ color: "#5d5f68", flexShrink: 0 }}>
                          →
                        </span>

                        {/* Shift */}
                        <Select
                          placeholder="Select Shift"
                          value={row.shiftId}
                          onChange={(val) =>
                            updatePatternRow(row.id, "shiftId", val)
                          }
                          style={{ flex: 1, minWidth: 180 }}
                          options={shiftOptions}
                          showSearch
                          optionFilterProp="label"
                        />

                        <span style={{ color: "#5d5f68", flexShrink: 0 }}>
                          →
                        </span>

                        <span
                          style={{
                            color: "#5d5f68",
                            fontSize: 13,
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Every
                        </span>

                        {/* Week numbers */}
                        <Select
                          mode="multiple"
                          placeholder="Weeks"
                          value={row.applyEvery}
                          onChange={(val) =>
                            updatePatternRow(row.id, "applyEvery", val)
                          }
                          style={{ minWidth: 140 }}
                          options={WEEK_OPTIONS}
                          maxTagCount="responsive"
                        />

                        {/* Remove */}
                        <button
                          type="button"
                          onClick={() => removePatternRow(row.id)}
                          disabled={patternRows.length === 1}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background:
                              patternRows.length === 1 ? "#ccc" : "#2d53da",
                            border: "none",
                            color: "#fff",
                            cursor:
                              patternRows.length === 1
                                ? "not-allowed"
                                : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            fontSize: 14,
                          }}
                        >
                          <MinusOutlined />
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="add-day-btn"
                      onClick={addPatternRow}
                    >
                      <PlusOutlined /> Add another day
                    </button>
                  </div>
                </Col>
              )}

              {/* ══════════════════════════════════════════
                  DAILY SETUP — shift + frequency
              ══════════════════════════════════════════ */}
              {(!isMultiSelect || setupType === "daily") && (
                <Col xs={24}>
                  <div
                    style={{
                      color: "#5d5f68",
                      fontWeight: 500,
                      marginBottom: 8,
                    }}
                  >
                    Select a new shift to assign
                  </div>

                  {isMultiSelect ? (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <Form.Item
                        name="shifttype"
                        style={{ flex: 1, marginBottom: 0 }}
                        rules={[
                          { required: true, message: "Shift type is required" },
                        ]}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select Shift"
                          options={shiftOptions}
                          getPopupContainer={() => containerRef.current}
                        />
                      </Form.Item>

                      <span
                        style={{
                          color: "#5d5f68",
                          fontSize: 18,
                          flexShrink: 0,
                        }}
                      >
                        →
                      </span>

                      <Select
                        value={frequency}
                        onChange={(val) => {
                          setFrequency(val);
                          setSelectedDays([]);
                          setApplyEvery([]);
                        }}
                        style={{ minWidth: 140 }}
                        options={FREQUENCY_OPTIONS}
                        getPopupContainer={() => containerRef.current}
                      />
                    </div>
                  ) : (
                    <Form.Item
                      className="mb-2 pb-0"
                      name="shifttype"
                      rules={[
                        {
                          required: true,
                          pattern: NON_EMPTY_REGEX,
                          message: "Shift type is required",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select Shift"
                        options={
                          shiftList
                            ?.filter((item) => item?.status === "active")
                            ?.map((val) => ({
                              label: val?.shifttitle,
                              value: val?.shift_id,
                            })) || []
                        }
                        getPopupContainer={() => containerRef.current}
                      />
                    </Form.Item>
                  )}
                </Col>
              )}

              {/* Timing box — single only */}
              {findShift && !isMultiSelect && (
                <Col xs={24}>
                  <div className="box p-2">
                    <span style={{ color: "#5d5f68" }}>Timing</span>
                    <span className="mx-3">
                      {moment(findShift?.starts).format("hh:mm A")} -{" "}
                      {moment(findShift?.ends).format("hh:mm A")}
                    </span>
                  </div>
                </Col>
              )}

              {/* ── Custom days ── */}
              {isMultiSelect &&
                setupType === "daily" &&
                frequency === "custom" && (
                  <>
                    <Col xs={24} className="mt-3">
                      <div style={{ color: "#5d5f68", fontWeight: 500 }}>
                        Choose the Custom Days
                        <span style={{ color: "red" }}> *</span>
                      </div>
                      <div className="day-picker">
                        {DAY_OPTIONS.map((day) => (
                          <div
                            key={day.value}
                            className={`day-btn ${
                              selectedDays.includes(day.value) ? "selected" : ""
                            }`}
                            onClick={() => toggleDay(day.value)}
                          >
                            {day.label.charAt(0)}
                          </div>
                        ))}
                      </div>
                      {selectedDays.length === 0 && (
                        <div
                          style={{ color: "red", fontSize: 12, marginTop: 4 }}
                        >
                          Please select at least one day
                        </div>
                      )}
                    </Col>

                    <Col xs={24} className="mt-3">
                      <Form.Item
                        label={
                          <span style={{ color: "#5d5f68", fontWeight: 500 }}>
                            Apply this Every{" "}
                            <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                      >
                        <Select
                          mode="multiple"
                          placeholder="Select weeks"
                          value={applyEvery}
                          onChange={setApplyEvery}
                          options={WEEK_OPTIONS}
                          style={{ width: "100%" }}
                          getPopupContainer={() => containerRef.current}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}

              {/* ── Notify email ── */}
              <Col xs={24} className="mt-2">
                <Form.Item name="notifyemail" valuePropName="checked">
                  <Checkbox style={{ color: "#5d5f68", fontWeight: 500 }}>
                    Notify these employees regarding assigned shift through
                    Email
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Spin>
    </StyledModal>
  );
};

export default AssignShift;
